import type { RootState } from '../redux/store'
import flatTexts from './flatTexts'

const TERM_DAYS_PLACEHOLDER = /\{\{\s*termDays\s*\}\}/g

/**
 * A payment option's term as display copy — "14 días" — or `undefined`.
 *
 * **One rule for the four screens that show a pick's term**: the picker's row
 * `tag` (COM-1254), and the payment row of `CartCheckoutPanel`,
 * `OrderConfirmationPanel` and `OrderDetailPanel` (COM-1710). Keeping it in one
 * place is what makes the template a single switch: a storefront that authors
 * `texts.paymentMethod.tagTermDays: ''` hides the term on every screen at once.
 *
 * - `termDays` must be a positive integer. It is `unknown` here because
 *   `ICheckoutRuleAttributes` declares only `label` and `helperText`, and the
 *   value arrives from untyped checkout-rule JSON — so `'14'`, `1.5`, `0`,
 *   `NaN` and `Infinity` are all reachable and all omit the tag.
 * - `template` must be a non-empty string. `{{termDays}}` in it is replaced;
 *   whitespace inside the braces is tolerated.
 *
 * The template is plural-always on purpose: no locale here distinguishes
 * `1 día` from `N días`, matching the fleet-wide `{{var}}` + `String.replace`
 * convention `minAmountProgressMessage` and `formatPromotionMessage` already
 * use.
 *
 * The substitution happens here rather than in config because injected panel
 * props pass through `processData` in `useCinnamonProps`, which would read a
 * leftover `{{termDays}}` as a config variable.
 */
export const formatTermDaysTag = (
  termDays: unknown,
  template: unknown,
): string | undefined => {
  if (typeof template !== 'string' || template.length === 0) return undefined
  if (
    typeof termDays !== 'number' ||
    !Number.isInteger(termDays) ||
    termDays <= 0
  ) {
    return undefined
  }

  return template.replace(TERM_DAYS_PLACEHOLDER, String(termDays))
}

/**
 * The storefront's `texts.paymentMethod.tagTermDays` — the template
 * `formatTermDaysTag` fills. All seven default locales ship one, so this is
 * `undefined` only where a storefront's config has no `paymentMethod` text
 * group at all.
 */
export const selectTermDaysTagTemplate = (
  state: RootState,
): string | undefined =>
  flatTexts(state?.defaultSlice?.config?.texts?.paymentMethod).tagTermDays
